import { useAccountSectionUnlocking } from "features/Account/utils/useAccountSectionChange";
import { notification_color_keys, useNotifications } from "utils/hooks";
import {
  automatedPayoutKeys,
  DAILY_CUSTOM_CYCLE_DAYS,
  DAYS_OF_THE_MONTH,
  FREQUENCY_KEYS,
  MINIMUM_WITHDRAWAL_AMOUNT,
  PAYOUT_AMOUNT_KEYS,
  WALLET_SETTING_KEYS,
  WEEK_DAYS_KEYS_MAP,
} from "../../../constants/Wallets.constants";
import { useWalletStore } from "../../../store/WalletStore";

export const useWalletSettings = () => {
  const { notify } = useNotifications();
  const { handleSectionChange } = useAccountSectionUnlocking();
  const automatedPayoutFormDate = useWalletStore(
    (state) => state.automatedPayoutFormDate
  );
  const isAutomatedPayoutModalOpen = useWalletStore(
    (state) => state.isAutomatedPayoutModalOpen
  );

  const updateAutomatatedPayoutSettings = ({ isAutomated }) => {
    let automatedPayoutValue = {
      [automatedPayoutKeys.isAutomated]: isAutomated,
      [automatedPayoutKeys.payoutType]:
        automatedPayoutFormDate[automatedPayoutKeys.payoutType],
      [automatedPayoutKeys.weeklyPayoutDay]: null,
      [automatedPayoutKeys.monthlyPayoutDay]: null,
      [automatedPayoutKeys.customCycleDays]: null,
      [automatedPayoutKeys.customSettlementAmount]: null,
    };
    if (
      automatedPayoutFormDate[automatedPayoutKeys.payoutType] ===
      FREQUENCY_KEYS.daily
    ) {
      automatedPayoutValue = {
        ...automatedPayoutValue,
        [automatedPayoutKeys.payoutType]: FREQUENCY_KEYS.custom,
        [automatedPayoutKeys.customCycleDays]: DAILY_CUSTOM_CYCLE_DAYS,
      };
    } else if (
      automatedPayoutFormDate[automatedPayoutKeys.payoutType] ===
      FREQUENCY_KEYS.weekly
    ) {
      automatedPayoutValue = {
        ...automatedPayoutValue,
        [automatedPayoutKeys.weeklyPayoutDay]:
          automatedPayoutFormDate?.[automatedPayoutKeys.weeklyPayoutDay],
      };
    } else if (
      automatedPayoutFormDate[automatedPayoutKeys.payoutType] ===
      FREQUENCY_KEYS.monthly
    ) {
      if (!automatedPayoutFormDate?.[automatedPayoutKeys.monthlyPayoutDay]) {
        notify(
          "Select a date for payout automation",
          notification_color_keys.error
        );
        return;
      }
      automatedPayoutValue = {
        ...automatedPayoutValue,
        [automatedPayoutKeys.monthlyPayoutDay]:
          automatedPayoutFormDate?.[automatedPayoutKeys.monthlyPayoutDay],
      };
    } else if (
      automatedPayoutFormDate[automatedPayoutKeys.payoutType] ===
      FREQUENCY_KEYS.custom
    ) {
      if (!automatedPayoutFormDate?.[automatedPayoutKeys.customCycleDays]) {
        notify(
          "Enter number of days for payout automation",
          notification_color_keys.error
        );
        return;
      }
      if (
        parseInt(
          automatedPayoutFormDate?.[automatedPayoutKeys.customCycleDays]
        ) === DAILY_CUSTOM_CYCLE_DAYS
      ) {
        notify(
          "Select daily in the case of 1 day as custom days",
          notification_color_keys.error
        );
        return;
      }
      automatedPayoutValue = {
        ...automatedPayoutValue,
        [automatedPayoutKeys.customCycleDays]: parseInt(
          automatedPayoutFormDate?.[automatedPayoutKeys.customCycleDays]
        ),
      };
    }

    if (
      automatedPayoutFormDate[automatedPayoutKeys.payoutAmount] ===
      PAYOUT_AMOUNT_KEYS.custom
    ) {
      if (
        !automatedPayoutFormDate?.[automatedPayoutKeys.customSettlementAmount]
      ) {
        notify(
          "Enter a custom payout amount before continuing.",
          notification_color_keys.error
        );
        return;
      }
      if (
        automatedPayoutFormDate?.[automatedPayoutKeys.customSettlementAmount] <
        MINIMUM_WITHDRAWAL_AMOUNT
      ) {
        notify(
          `Minimum amount for withdrawal is ${MINIMUM_WITHDRAWAL_AMOUNT}`,
          notification_color_keys.error
        );
        return;
      }
      automatedPayoutValue = {
        ...automatedPayoutValue,
        [automatedPayoutKeys.customSettlementAmount]:
          automatedPayoutFormDate?.[automatedPayoutKeys.customSettlementAmount],
      };
    }

    handleSettingsSectionValueChange(
      WALLET_SETTING_KEYS.AUTOMATED_PAYOUTS_CONFIG,
      automatedPayoutValue
    );
    isAutomatedPayoutModalOpen(false);
  };

  const handleSettingsSectionValueChange = (featureKey, featureValue) => {
    const payload = {
      feature_key: featureKey,
      feature_value: featureValue,
    };
    handleSectionChange({ payload });
  };

  const getPayoutHappenString = (payoutValue) => {
    let payoutHappenString = "";
    if (
      payoutValue?.[automatedPayoutKeys.payoutType] === FREQUENCY_KEYS.custom &&
      payoutValue?.[automatedPayoutKeys.customCycleDays] ===
        DAILY_CUSTOM_CYCLE_DAYS
    ) {
      payoutHappenString = payoutHappenString + "Daily";
    } else if (
      payoutValue?.[automatedPayoutKeys.payoutType] === FREQUENCY_KEYS.weekly
    ) {
      payoutHappenString =
        payoutHappenString +
        `Every week, on ${
          WEEK_DAYS_KEYS_MAP[payoutValue?.[automatedPayoutKeys.weeklyPayoutDay]]
        }`;
    } else if (
      payoutValue?.[automatedPayoutKeys.payoutType] === FREQUENCY_KEYS.monthly
    ) {
      let dayValue = "";
      if (
        payoutValue?.[automatedPayoutKeys.monthlyPayoutDay] ==
        DAYS_OF_THE_MONTH.LAST_DAY
      )
        dayValue = "Last day";
      else if (
        payoutValue?.[automatedPayoutKeys.monthlyPayoutDay] ==
        DAYS_OF_THE_MONTH.FIRST
      )
        dayValue = "1st";
      else if (
        payoutValue?.[automatedPayoutKeys.monthlyPayoutDay] ==
        DAYS_OF_THE_MONTH.SECOND
      )
        dayValue = "2nd";
      else if (
        payoutValue?.[automatedPayoutKeys.monthlyPayoutDay] ==
        DAYS_OF_THE_MONTH.THIRD
      )
        dayValue = "3rd";
      else
        dayValue = `${payoutValue?.[automatedPayoutKeys.monthlyPayoutDay]}th`;
      payoutHappenString = payoutHappenString + `On ${dayValue} of every month`;
    } else if (
      payoutValue?.[automatedPayoutKeys.payoutType] === FREQUENCY_KEYS.custom
    ) {
      payoutHappenString =
        payoutHappenString +
        `In every ${payoutValue?.[automatedPayoutKeys.customCycleDays]} days`;
    }

    if (!payoutValue?.[automatedPayoutKeys.customSettlementAmount]) {
      payoutHappenString = payoutHappenString + ", for full amount in wallet";
    }

    return payoutHappenString;
  };

  const getInitialPayoutType = ({ payoutConfig }) => {
    const payoutType = payoutConfig?.[automatedPayoutKeys.payoutType];
    const customCycleDays = payoutConfig?.[automatedPayoutKeys.customCycleDays];

    // default fallback
    if (!payoutType) {
      return FREQUENCY_KEYS.daily;
    }

    // custom cycle of 1 day is effectively daily
    if (payoutType === FREQUENCY_KEYS.custom && customCycleDays === 1) {
      return FREQUENCY_KEYS.daily;
    }

    return payoutType;
  };

  return {
    updateAutomatatedPayoutSettings,
    handleSettingsSectionValueChange,
    getPayoutHappenString,
    getInitialPayoutType,
  };
};
